import { Sparkles } from 'lucide-react';
import { PbtsTagChips } from '@/components/pbts/PbtsTagChips';
import { PbtsTagList } from '@/components/pbts/PbtsTagList';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';
import { useTypewriterValue } from './useTypewriterValue';

const GENERATED_SUMMARY_TOOLTIP =
	'Esse resumo foi gerado a partir da transcrição da sessão';

export type ProntuarioFieldVariant = 'dark' | 'light';

export type ChoiceOption = { value: string; label: string };

/**
 * Contrato comum exposto pelos editores de prontuário (sessão e medical_record)
 * via `onEditorStateChange`.
 * - `save()`: salva com toasts (usado pelo ProfessionalProntuarioModal).
 * - `saveAsync()`: salva sem toasts e rejeita em erro (usado pela revisão
 *   pós-sessão, que controla feedback e a tela de sucesso).
 */
export type ProntuarioEditorState = {
	hasChanges: boolean;
	isPending: boolean;
	save: () => void;
	saveAsync: () => Promise<void>;
};

const INLINE_SELECT_LABEL_CLASS = {
	dark: 'text-[10px] font-semibold uppercase tracking-wide text-slate-500',
	light: 'text-[10px] font-semibold uppercase tracking-wide text-zinc-400',
} as const;

const INLINE_SELECT_TRIGGER_CLASS = {
	dark: 'h-8 w-full min-w-0 rounded-lg border-slate-600 bg-slate-800/60 px-2.5 text-sm text-slate-200 focus:ring-slate-600/40',
	light: 'h-8 w-full min-w-0 rounded-lg border-zinc-200 bg-white px-2.5 text-sm text-zinc-800 focus:ring-zinc-200/80',
} as const;

const INLINE_SELECT_WIDTH_CLASS = 'w-[9.5rem]';

const SESSION_FIELD_CONTAINER_CLASS = {
	light: 'overflow-hidden rounded-xl border border-zinc-200 bg-zinc-50',
	dark: 'overflow-hidden rounded-xl border border-slate-600/80 bg-slate-800/60',
} as const;

const SESSION_FIELD_CONTAINER_TITLE_ROW_CLASS = {
	light: 'mb-2 flex items-center justify-between gap-2 px-3.5 pt-3',
	dark: 'mb-2 flex items-center justify-between gap-2 px-3.5 pt-3',
} as const;

const SESSION_FIELD_CONTAINER_TITLE_CLASS = {
	light: 'text-[12px] font-bold uppercase tracking-wide text-zinc-900',
	dark: 'text-[12px] font-bold uppercase tracking-wide text-slate-100',
} as const;

const SESSION_FIELD_CONTAINER_EDITABLE_BADGE_CLASS = {
	light: 'shrink-0 text-[9px] font-medium uppercase tracking-wide text-zinc-400',
	dark: 'shrink-0 text-[9px] font-medium uppercase tracking-wide text-slate-500',
} as const;

const SESSION_FIELD_TEXTAREA_CLASS =
	'w-full rounded-none border-0 bg-zinc-50 px-3.5 pb-3 pt-0 text-[13px] text-zinc-700 placeholder:text-zinc-500 focus:outline-none focus:ring-0 resize-none leading-relaxed';

const SESSION_FIELD_TEXTAREA_DARK_CLASS =
	'w-full rounded-none border-0 bg-transparent px-3.5 pb-3 pt-0 text-[13px] text-slate-400 placeholder:text-slate-600 focus:outline-none focus:ring-0 resize-none leading-relaxed';

const SESSION_FIELD_TAGS_CLASS = 'px-3.5 pb-3 pt-2';

const GENERATED_SUMMARY_CONTAINER_CLASS = {
	light: 'rounded-xl border border-purple-900 bg-purple-50 px-3.5 py-3',
	dark: 'rounded-xl border border-purple-400/25 bg-purple-500/10 px-3.5 py-3',
} as const;

const GENERATED_SUMMARY_TITLE_CLASS = {
	light: 'text-[11px] font-semibold uppercase tracking-wide text-purple-900',
	dark: 'text-[11px] font-semibold uppercase tracking-wide text-purple-200',
} as const;

const GENERATED_SUMMARY_BODY_CLASS = {
	light: 'mt-2 text-[14px] leading-[1.6] text-purple-900',
	dark: 'mt-2 text-[14px] leading-[1.6] text-purple-100/85',
} as const;

const GENERATED_SUMMARY_SPARKLE_CLASS = {
	light: 'text-purple-900',
	dark: 'text-purple-300',
} as const;

export const InlineSelectField = ({
	label,
	value,
	options,
	onChange,
	variant = 'dark',
}: {
	label: string;
	value: string;
	options: ChoiceOption[];
	onChange: (v: string) => void;
	variant?: ProntuarioFieldVariant;
}) => {
	const selectedLabel =
		options.find((option) => option.value === value)?.label ?? value;

	return (
		<div className={cn('min-w-0 space-y-1', INLINE_SELECT_WIDTH_CLASS)}>
			<label className={INLINE_SELECT_LABEL_CLASS[variant]}>
				{label}
			</label>
			<Select
				value={value}
				onValueChange={onChange}
				className={INLINE_SELECT_WIDTH_CLASS}
			>
				<SelectTrigger className={INLINE_SELECT_TRIGGER_CLASS[variant]}>
					<SelectValue className="truncate">
						{selectedLabel}
					</SelectValue>
				</SelectTrigger>
				<SelectContent dropdownWidth="max-content">
					{options.map((option) => (
						<SelectItem
							key={option.value}
							value={option.value}
							noTruncate
						>
							{option.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
		</div>
	);
};

export type SessionFieldContainerProps = {
	label: string;
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	rows?: number;
	tagValue?: string;
	onTagChange?: (value: string) => void;
	variant?: ProntuarioFieldVariant;
	/**
	 * Liga a animação de digitação do valor (ver {@link useTypewriterValue}).
	 * Hoje é um no-op; deixado pronto para o efeito futuro.
	 */
	animateValue?: boolean;
	tutorialDemoAddTagTarget?: string;
	tutorialDemoEditorTarget?: string;
	onTutorialDemoAddTagOpen?: () => void;
	onTutorialDemoEditorClose?: () => void;
};

export const SessionFieldContainer = ({
	label,
	value,
	onChange,
	placeholder,
	rows = 2,
	tagValue,
	onTagChange,
	variant = 'light',
	animateValue = false,
	tutorialDemoAddTagTarget,
	tutorialDemoEditorTarget,
	onTutorialDemoAddTagOpen,
	onTutorialDemoEditorClose,
}: SessionFieldContainerProps) => {
	const pbtsChipVariant = variant === 'light' ? 'light' : 'dark';
	const displayValue = useTypewriterValue(value, animateValue);

	return (
		<div className={SESSION_FIELD_CONTAINER_CLASS[variant]}>
			<div className={SESSION_FIELD_CONTAINER_TITLE_ROW_CLASS[variant]}>
				<label className={SESSION_FIELD_CONTAINER_TITLE_CLASS[variant]}>
					{label}
				</label>
				<span
					className={
						SESSION_FIELD_CONTAINER_EDITABLE_BADGE_CLASS[variant]
					}
				>
					editável
				</span>
			</div>
			<textarea
				value={displayValue}
				onChange={(event) => onChange(event.target.value)}
				placeholder={placeholder}
				rows={rows}
				className={
					variant === 'light'
						? SESSION_FIELD_TEXTAREA_CLASS
						: SESSION_FIELD_TEXTAREA_DARK_CLASS
				}
			/>
			{onTagChange ? (
				<PbtsTagList
					value={tagValue}
					onChange={onTagChange}
					editable
					variant={pbtsChipVariant}
					editorVariant="light"
					className={SESSION_FIELD_TAGS_CLASS}
					tutorialDemoAddTagTarget={tutorialDemoAddTagTarget}
					tutorialDemoEditorTarget={tutorialDemoEditorTarget}
					onTutorialDemoAddTagOpen={onTutorialDemoAddTagOpen}
					onTutorialDemoEditorClose={onTutorialDemoEditorClose}
				/>
			) : null}
		</div>
	);
};

export type GeneratedSummaryBlockProps = {
	summary: string;
	summaryTag?: string | null;
	variant?: ProntuarioFieldVariant;
};

export const GeneratedSummaryBlock = ({
	summary,
	summaryTag,
	variant = 'light',
}: GeneratedSummaryBlockProps) => (
	<div className={GENERATED_SUMMARY_CONTAINER_CLASS[variant]}>
		<div className="flex w-fit items-center gap-1.5">
			<p className={GENERATED_SUMMARY_TITLE_CLASS[variant]}>
				Resumo da sessão
			</p>
			<Tooltip
				content={GENERATED_SUMMARY_TOOLTIP}
				position="bottom"
				className="max-w-[260px] whitespace-normal text-center"
			>
				<span
					className="inline-flex cursor-default text-inherit"
					tabIndex={0}
					role="img"
					aria-label={GENERATED_SUMMARY_TOOLTIP}
				>
					<Sparkles
						className={cn(
							'size-3.5 shrink-0',
							GENERATED_SUMMARY_SPARKLE_CLASS[variant],
						)}
						aria-hidden
					/>
				</span>
			</Tooltip>
		</div>
		<p className={GENERATED_SUMMARY_BODY_CLASS[variant]}>{summary}</p>
		<PbtsTagChips
			value={summaryTag}
			variant={variant === 'light' ? 'light' : 'dark'}
			className="mt-2"
		/>
	</div>
);
